import type { SingleFlightItineraryResponse } from "@b2bportal/air-booking-api";
import type { AirChfarExerciseQuoteResponseExerciseAvailable } from "@b2bportal/air-chfar-api";

const getChfarSlicesFromItinerary = ({
  itinerary,
  context,
}: SingleFlightItineraryResponse) =>
  itinerary.bookedItinerary.travelItinerary.slices.map((slice) => {
    const firstSegment = slice.segments[0];
    const lastSegment = slice.segments[slice.segments.length - 1];
    return {
      originName:
        context.airports[firstSegment.origin.locationCode].servedCityName,
      originCode: firstSegment.origin.locationCode,
      departureDate: firstSegment.scheduledDeparture,
      destinationName:
        context.airports[lastSegment.destination.locationCode].servedCityName,
      destinationCode: lastSegment.destination.locationCode,
      arrivalDate: lastSegment.scheduledArrival,
      stops: slice.segments.length - 1,
      airlines: [
        ...new Set(
          slice.segments.map((segment) => segment.marketingAirline.code)
        ),
      ].map((code) => ({
        name: context.airlines[code].displayName,
        code,
      })),
    };
  });

const getChfarFlightRouteFromItinerary = ({
  itinerary,
  context,
}: SingleFlightItineraryResponse) => {
  const availableSlices = itinerary.bookedItinerary.travelItinerary.slices;
  const departureSlice = availableSlices[0];
  const originLocationCode = departureSlice.segments[0].origin.locationCode;
  const destinationLocationCode =
    departureSlice.segments[departureSlice.segments.length - 1].destination
      .locationCode;

  return {
    origin: {
      name: context.airports[originLocationCode].servedCityName,
      code: originLocationCode,
      regionType: "airport",
    },
    destination: {
      name: context.airports[destinationLocationCode].servedCityName,
      code: destinationLocationCode,
      regionType: "airport",
    },
  };
};
const getChfarFlightDatesFromItinerary = (
  itinerary: SingleFlightItineraryResponse["itinerary"]
) => {
  const availableSlices = itinerary.bookedItinerary.travelItinerary.slices;
  const firstSlice = availableSlices[0];
  const lastSlice = availableSlices[availableSlices.length - 1];

  return {
    departureDate: firstSlice.segments[0].scheduledDeparture,
    ...(availableSlices.length > 1 && {
      returnDate:
        lastSlice.segments[lastSlice.segments.length - 1].scheduledDeparture,
    }),
  };
};

const getChfarPassengersFromItinerary = (
  itinerary: SingleFlightItineraryResponse["itinerary"]
) => {
  const { alone, withLapInfants } = itinerary.bookedItinerary.passengers;
  return {
    adults: Number(
      alone.filter((passenger) => passenger.type === "ADT").length +
        withLapInfants.length
    ),
    children: Number(
      alone.filter((passenger) => passenger.type === "CNN").length
    ),
    infantInSeat: Number(
      alone.filter((passenger) => passenger.type === "INS").length
    ),
    infantInLap: Number(withLapInfants.length),
  };
};

const getChfarDataFromQuote = ({
  itineraryResponse,
  coverage,
}: AirChfarExerciseQuoteResponseExerciseAvailable) => {
  const { itinerary: itineraryOpaque, context } = itineraryResponse;

  const { itinerary } = itineraryOpaque.value as {
    itinerary: SingleFlightItineraryResponse["itinerary"];
  };

  return {
    slices: getChfarSlicesFromItinerary({ itinerary, context }),
    coverage,
    flightRoute: getChfarFlightRouteFromItinerary({ itinerary, context }),
    flightDates: getChfarFlightDatesFromItinerary(itinerary),
    passengers: getChfarPassengersFromItinerary(itinerary),
    passengerDetails: itinerary.bookedItinerary.passengers,
  };
};

export default getChfarDataFromQuote;
